import * as Sentry from '@sentry/react';

const VERSION_MANIFEST_PATH = 'version.json';

// Minimum gap between checks triggered by visibilitychange — avoids hammering
// the manifest on rapid tab flips while still catching tabs backgrounded long
// enough for a deploy to land.
const VISIBILITY_DEBOUNCE_MS = 60_000;

const RELOAD_GUARD_KEY = 'ysh:versionCheck:reloads';
// If more than RELOAD_GUARD_MAX reloads happen inside this window we stop
// reloading and emit a Sentry warning. Defends against webviews that
// hard-cache index.html and would otherwise loop forever.
const RELOAD_GUARD_WINDOW_MS = 60_000;
const RELOAD_GUARD_MAX = 2;

type ReloadGuard = { firstAt: number; count: number };

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

type CaptureMessageOptions = {
  level: 'warning';
  extra: Record<string, unknown>;
};

export type VersionCheckerDeps = {
  bakedVersion: string;
  baseUrl: string;
  now: () => number;
  storage: StorageLike;
  fetch: typeof fetch;
  reload: (url: string) => void;
  captureMessage: (message: string, options: CaptureMessageOptions) => void;
};

export type VersionChecker = {
  start: () => void;
  stop: () => void;
};

export const createVersionChecker = (
  deps: VersionCheckerDeps
): VersionChecker => {
  const { bakedVersion, baseUrl, now, storage, fetch, reload, captureMessage } =
    deps;

  let lastCheckAt = 0;
  let inFlight = false;
  let isStarted = false;

  const readGuard = (): ReloadGuard => {
    try {
      const raw = storage.getItem(RELOAD_GUARD_KEY);
      if (!raw) return { firstAt: 0, count: 0 };
      return JSON.parse(raw) as ReloadGuard;
    } catch {
      return { firstAt: 0, count: 0 };
    }
  };

  // Single read-modify-write: returns true and reserves a reload slot if
  // budget remains, false if exhausted. One pass closes the TOCTOU window
  // and the previous double sessionStorage read.
  const tryConsumeReloadBudget = (): boolean => {
    const guard = readGuard();
    const t = now();
    const fresh = t - guard.firstAt > RELOAD_GUARD_WINDOW_MS;
    const nextCount = fresh ? 1 : guard.count + 1;
    if (nextCount > RELOAD_GUARD_MAX) return false;
    try {
      storage.setItem(
        RELOAD_GUARD_KEY,
        JSON.stringify({
          firstAt: fresh ? t : guard.firstAt,
          count: nextCount
        })
      );
    } catch {
      // sessionStorage full / disabled — guard becomes best-effort, reload proceeds.
    }
    return true;
  };

  const runCheck = async (): Promise<void> => {
    if (inFlight) return;
    inFlight = true;
    try {
      const url = `${baseUrl}${VERSION_MANIFEST_PATH}?t=${now()}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: unknown };
      const remote = typeof data.version === 'string' ? data.version : null;
      if (!remote || remote === bakedVersion) return;
      forceReload();
    } catch {
      // Manifest check must never crash the app — silent swallow is intentional.
    } finally {
      // Trailing-edge debounce: anchor the next-check gate to completion, not
      // initiation, so a slow fetch can't open the gate for a parallel check.
      lastCheckAt = now();
      inFlight = false;
    }
  };

  const forceReload = (): void => {
    if (!tryConsumeReloadBudget()) {
      captureMessage(
        'versionCheck: reload guard tripped — version mismatch persists',
        { level: 'warning', extra: { baked: bakedVersion } }
      );
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('_v', String(now()));
    reload(url.toString());
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (now() - lastCheckAt < VISIBILITY_DEBOUNCE_MS) return;
    void runCheck();
  };

  return {
    start: () => {
      if (isStarted) return;
      isStarted = true;
      void runCheck();
      document.addEventListener('visibilitychange', onVisibilityChange);
    },
    stop: () => {
      isStarted = false;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
};

export const startVersionCheck = (): void => {
  const checker = createVersionChecker({
    bakedVersion: __APP_VERSION__,
    baseUrl: import.meta.env.BASE_URL,
    now: () => Date.now(),
    storage: sessionStorage,
    fetch: window.fetch.bind(window),
    reload: url => window.location.replace(url),
    captureMessage: (message, options) =>
      Sentry.captureMessage(message, options)
  });
  checker.start();
};
